// =============================================
// USER PROFILE & MULTI-USER REGISTRY
// Tracks EVERY individual who uses the app.
// Stores full per-user analytics in localStorage.
// syncs with Firebase Cloud if configured.
// =============================================
import { syncUserToCloud } from './firebase.js';

const REGISTRY_KEY = 'carb-user-registry';
const CURRENT_USER_KEY = 'carb-current-user-id';

// Generate a simple unique ID
function genId() {
    return 'u_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 7);
}

// ===== REGISTRY (all users) =====

function loadRegistry() {
    try {
        const raw = localStorage.getItem(REGISTRY_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
}

export function saveRegistry(reg) {
    try { 
        localStorage.setItem(REGISTRY_KEY, JSON.stringify(reg)); 
        
        // Push the active user's updated data to the Firebase cloud
        const currentId = getCurrentUserId();
        if (currentId && reg[currentId]) {
            syncUserToCloud(reg[currentId]).catch(e => console.error("Cloud sync skipped/failed:", e));
        }
    } catch {}
}

export function getAllUsers() {
    return loadRegistry();
}

export function getUserById(id) {
    const reg = loadRegistry();
    return reg[id] || null;
}

// ===== CURRENT USER =====

export function getCurrentUserId() {
    const id = localStorage.getItem(CURRENT_USER_KEY);
    if (!id || id === 'undefined' || id === 'null') return null;
    return id;
}

export function isFirstVisit() {
    const id = getCurrentUserId();
    const reg = loadRegistry();
    if (!id || !reg[id]) return true;
    
    // Even if ID exists, if name is missing or they are still a guest, it's a 'first visit' for registration purposes
    if (reg[id].isGuest) return true;
    
    const name = reg[id].name;
    if (!name || name.trim() === '' || name.includes('Candidate')) return true;
    
    // Safety check: is registry entry corrupt/empty?
    if (!reg[id].createdAt) return true;
    
    return false;
}

export function getUserName() {
    const id = getCurrentUserId();
    if (!id) return null;
    const user = getUserById(id);
    return user ? user.name : null;
}

export function getProfile() {
    const id = getCurrentUserId();
    if (!id) return null;
    return getUserById(id);
}

// ===== REGISTRATION =====

export function setUserName(name) {
    let id = getCurrentUserId();
    const reg = loadRegistry();

    if (id && reg[id]) {
        // Promote guest or just update name
        reg[id].name = name;
        reg[id].isGuest = false;
        saveRegistry(reg);
        return id;
    }

    // New user (should rarely happen with ensureGuestSession)
    id = genId();
    reg[id] = initUserData(id, name);
    saveRegistry(reg);
    localStorage.setItem(CURRENT_USER_KEY, id);
    return id;
}

function initUserData(id, name, isGuest = false) {
    return {
        id,
        name: name || ('Candidate ' + id.slice(-4).toUpperCase()),
        isGuest,
        createdAt: new Date().toISOString(),
        lastVisit: new Date().toISOString(),
        visits: 1,
        totalTimeMs: 0,
        sessions: [],
        pageVisits: {},
        pageTime: {},
        featureUsage: {},
        quiz: {
            accuracy: 0,
            totalAnswered: 0,
            totalCorrect: 0,
            xp: 0,
            level: 1,
            streakDays: 0,
        },
        flashcards: {
            good: 0,
            maybe: 0,
            bad: 0,
            totalSeen: 0,
            unrated: 0
        },
    };
}

/**
 * Ensures a session ID exists from the very first page load.
 * This captures 'gold' data from users who haven't registered yet.
 */
/**
 * Ensures a session ID exists from the very first page load.
 * POWERFUL: Now auto-recognizes browsers to avoid 'Anonymous Pilot' resets.
 * If the current ID is lost/cleared, it checks the registry for a previously registered user.
 */
export function ensureGuestSession() {
    let id = getCurrentUserId();
    const reg = loadRegistry();
    
    // Identity Bridging: if we lost our cookie/ID, look for our old registry entry
    if (!id || !reg[id]) {
        const knownIds = Object.keys(reg);
        if (knownIds.length > 0) {
            const bestMatch = knownIds
                .map(k => reg[k])
                .filter(u => !u.isGuest && u.name && !u.name.includes('Candidate'))
                .sort((a,b) => new Date(b.lastVisit) - new Date(a.lastVisit))[0];
            
            if (bestMatch) {
                localStorage.setItem(CURRENT_USER_KEY, bestMatch.id);
                return bestMatch.id;
            }
        }
    }

    if (id && reg[id]) return id;

    id = 'guest_' + Math.random().toString(36).slice(2, 8);
    reg[id] = initUserData(id, 'Anonymous Pilot', true);
    
    saveRegistry(reg);
    localStorage.setItem(CURRENT_USER_KEY, id);
    return id;
}

// ===== VISIT TRACKING =====

export function recordVisit() {
    const id = getCurrentUserId();
    if (!id) return;
    const reg = loadRegistry();
    if (!reg[id]) return;
    reg[id].visits = (reg[id].visits || 0) + 1;
    reg[id].lastVisit = new Date().toISOString();
    saveRegistry(reg);
}

// ===== PER-USER DATA UPDATES =====

export function updateUserPageVisit(pageId) {
    const id = getCurrentUserId();
    if (!id) return;
    const reg = loadRegistry();
    if (!reg[id]) return;
    if (!reg[id].pageVisits) reg[id].pageVisits = {};
    reg[id].pageVisits[pageId] = (reg[id].pageVisits[pageId] || 0) + 1;
    saveRegistry(reg);
}

export function updateUserPageTime(pageId, elapsedMs) {
    const id = getCurrentUserId();
    if (!id) return;
    const reg = loadRegistry();
    if (!reg[id]) return;
    reg[id].pageTime[pageId] = (reg[id].pageTime[pageId] || 0) + elapsedMs;
    // Also update global total
    if (elapsedMs > 0) {
        reg[id].totalTimeMs = (reg[id].totalTimeMs || 0) + elapsedMs;
    }
    saveRegistry(reg);
}

export function updateUserFeature(featureName) {
    const id = getCurrentUserId();
    if (!id) return;
    const reg = loadRegistry();
    if (!reg[id]) return;
    if (!reg[id].featureUsage) reg[id].featureUsage = {};
    reg[id].featureUsage[featureName] = (reg[id].featureUsage[featureName] || 0) + 1;
    saveRegistry(reg);
}

export function recordUserSession(sessionData) {
    const id = getCurrentUserId();
    if (!id) return;
    const reg = loadRegistry();
    if (!reg[id]) return;
    if (!reg[id].sessions) reg[id].sessions = [];
    
    if (sessionData.isHeartbeat && reg[id].sessions.length > 0) {
        // Update the last session instead of adding a new one
        reg[id].sessions[reg[id].sessions.length - 1] = sessionData;
    } else {
        reg[id].sessions.push(sessionData);
    }
    
    // Cap at 100
    if (reg[id].sessions.length > 100) reg[id].sessions = reg[id].sessions.slice(-100);
    saveRegistry(reg);
}

// Snapshot quiz analytics into the user's registry entry
export function syncQuizToUser() {
    const id = getCurrentUserId();
    if (!id) return;
    const reg = loadRegistry();
    if (!reg[id]) return;
    try {
        const key = `carb-quiz-analytics_${id}`;
        const raw = localStorage.getItem(key);
        if (raw) {
            reg[id].quiz = JSON.parse(raw);
            saveRegistry(reg);
        }
    } catch {}
}

// Snapshot flashcard analytics into the user registry
export function syncFlashcardsToUser() {
    const id = getCurrentUserId();
    if (!id) return;
    const reg = loadRegistry();
    if (!reg[id]) return;
    try {
        const key = 'carb-flashcard-state'; // Global key used in flashcards.js
        const raw = localStorage.getItem(key);
        if (raw) {
            const fcState = JSON.parse(raw);
            const ratings = fcState.ratings || {};
            let good = 0, maybe = 0, bad = 0;
            Object.values(ratings).forEach(r => {
                if (r === 'good') good++;
                else if (r === 'maybe') maybe++;
                else if (r === 'bad') bad++;
            });
            
            reg[id].flashcards = {
                good, maybe, bad,
                totalSeen: Object.keys(ratings).length,
                ratings: ratings, // Store full ratings map for admin to recalculate
                studyHistory: fcState.studyHistory || []
            };
            saveRegistry(reg);
        }
    } catch (e) {
        console.error("Flashcard sync failed:", e);
    }
}

// ===== ADMIN HELPERS =====

export function resetProfile() {
    const id = getCurrentUserId();
    localStorage.removeItem(CURRENT_USER_KEY);
    // Keep their data in registry — admin can still see it
}

export function deleteUser(userId) {
    const reg = loadRegistry();
    delete reg[userId];
    saveRegistry(reg);
    // If deleting current user, clear current
    if (getCurrentUserId() === userId) {
        localStorage.removeItem(CURRENT_USER_KEY);
    }
}

// ===== REMOTE SYNC BRIDGE =====

/**
 * Merges an external registry into the local one.
 * Used for the "Classroom Hub" feature.
 */
export function mergeRegistry(incomingReg) {
    const local = loadRegistry();
    let mergedCount = 0;
    
    for (const [id, user] of Object.entries(incomingReg)) {
        if (!local[id] || new Date(user.lastVisit) > new Date(local[id].lastVisit)) {
            local[id] = user;
            mergedCount++;
        }
    }
    
    saveRegistry(local);
    return mergedCount;
}

/**
 * Packs the entire registry into a portable string
 */
export function exportRegistryString() {
    const reg = loadRegistry();
    return btoa(JSON.stringify(reg));
}

/**
 * Unpacks a registry string for merging
 */
export function importRegistryString(str) {
    try {
        const reg = JSON.parse(atob(str));
        return mergeRegistry(reg);
    } catch (e) {
        console.error('Failed to import registry:', e);
        return 0;
    }
}

export function getGreeting() {
    const name = getUserName();
    if (!name) return '';
    const hours = new Date().getHours();
    let timeGreeting = "Good evening";
    if (hours < 12) timeGreeting = "Good morning";
    else if (hours < 18) timeGreeting = "Good afternoon";
    return `${timeGreeting}, ${name}`;
}
